import { cn } from "@/lib/utils";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Typography,
} from "@st-marketplace/ui";
import { type ReactNode, useContext } from "react";
import type {
	Control,
	ControllerRenderProps,
	FieldValues,
	Path,
} from "react-hook-form";
import { SettingsLayoutContext } from "./SettingsCard";

const ROW_CLASS = {
	rows: "grid items-start gap-2 py-5 first:pt-0 last:pb-0 sm:grid-cols-[1fr_20rem] sm:gap-8",
	// two per row on xl (cells placed with `className`), label above the control
	columns:
		"grid items-start gap-2 py-5 first:pt-0 last:pb-0 sm:grid-cols-[1fr_20rem] sm:gap-8 xl:flex xl:flex-col xl:items-stretch xl:gap-2 xl:py-0",
	compact: "flex flex-col gap-2 py-4 first:pt-0 last:pb-0",
};

const useRowStyle = () => {
	const layout = useContext(SettingsLayoutContext);
	const compact = layout === "compact";
	return {
		row: ROW_CLASS[layout],
		control: "min-w-0",
		title: compact ? ("body-sm" as const) : ("title-sm" as const),
		titleClassName: compact ? "font-medium" : undefined,
		description: compact ? ("caption" as const) : ("body-sm" as const),
	};
};

type SettingsRowProps = {
	className?: string;
	title: ReactNode;
	description?: ReactNode;
	children: ReactNode;
};

/** Label + helper text on the start side, control on the end side. */
export const SettingsRow: React.FC<SettingsRowProps> = ({
	className,
	title,
	description,
	children,
}) => {
	const style = useRowStyle();
	return (
		<div className={cn(style.row, className)}>
			<div className="min-w-0">
				<Typography variant={style.title} className={style.titleClassName}>
					{title}
				</Typography>
				{description ? (
					<Typography
						variant={style.description}
						className="mt-1 text-secondary-text-100"
					>
						{description}
					</Typography>
				) : null}
			</div>
			<div className={style.control}>{children}</div>
		</div>
	);
};

type SettingsFieldProps<T extends FieldValues, N extends Path<T>> = {
	className?: string;
	control: Control<T>;
	name: N;
	title: ReactNode;
	description?: ReactNode;
	render: (field: ControllerRenderProps<T, N>) => React.ReactElement;
};

/** Same row, but the title doubles as the field label (no duplicated captions). */
export function SettingsField<T extends FieldValues, N extends Path<T>>({
	className,
	control,
	name,
	title,
	description,
	render,
}: SettingsFieldProps<T, N>) {
	const style = useRowStyle();
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem className={cn(style.row, className)}>
					<div className="min-w-0">
						<FormLabel className="mb-0 text-secondary-text">
							<Typography
								variant={style.title}
								as="span"
								className={style.titleClassName}
							>
								{title}
							</Typography>
						</FormLabel>
						{description ? (
							<Typography
								variant={style.description}
								className="mt-1 text-secondary-text-100"
							>
								{description}
							</Typography>
						) : null}
					</div>
					<div className={style.control}>
						<FormControl>{render(field)}</FormControl>
						<FormMessage />
					</div>
				</FormItem>
			)}
		/>
	);
}
